/**
 * Meta Configuration Page
 * 
 * Centralized page for managing Meta Ads account connection and financial settings
 */

import { useState, useEffect, useCallback } from 'react'
import * as Icons from 'lucide-react'
import { useMetaConnection } from '@/shared/contexts'
import { metaApi } from '@/shared/services'
import apiClient from '@/shared/lib/api'
import { useAuth } from '@trafiflow/shared-ui'

// Financial Settings types
interface FinancialSettings {
  tenant_id: string
  cogs_percentage: number // 0.0 to 1.0
  updated_at: string
  updated_by?: string
}

// Simple type for ad accounts from API
interface AdAccount {
  id: string
  account_id: string
  name: string
  currency: string
}

// ============================================================================
// Connection Form Component
// ============================================================================

function ConnectionForm({ onSuccess }: { onSuccess: () => void }) {
  const { connect } = useMetaConnection()
  const [accessToken, setAccessToken] = useState('')
  const [adAccountId, setAdAccountId] = useState('')
  const [adAccountName, setAdAccountName] = useState('')
  const [adAccountCurrency, setAdAccountCurrency] = useState('')
  const [isConnecting, setIsConnecting] = useState(false)
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([])
  const [showAccountSelector, setShowAccountSelector] = useState(false)

  // Fetch ad accounts using the token
  const handleFetchAccounts = async () => {
    if (!accessToken) {
      setError('Please enter an access token first')
      return
    }

    setIsFetchingAccounts(true)
    setError(null)

    try {
      const response = await metaApi.getAdAccounts(accessToken)
      if (response.success && response.data && response.data.length > 0) {
        setAdAccounts(response.data)
        setShowAccountSelector(true)
      } else {
        setError('No ad accounts found for this token. Please check permissions.')
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      setError(err.message || 'Failed to fetch ad accounts')
    } finally {
      setIsFetchingAccounts(false)
    }
  }

  // Handle account selection
  const handleSelectAccount = (account: AdAccount) => {
    setAdAccountId(account.account_id)
    setAdAccountName(account.name)
    setAdAccountCurrency(account.currency)
    setShowAccountSelector(false)
  }

  // Handle form submission
  const handleConnect = async () => {
    if (!accessToken || !adAccountId) {
      setError('Please provide both access token and ad account ID')
      return
    }

    setIsConnecting(true)
    setError(null)

    try {
      await connect(accessToken, adAccountId, adAccountName, adAccountCurrency)
      onSuccess()
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string }
      setError(err.message || 'Failed to connect. Please check your credentials.')
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
      <div className="px-6 py-4 border-b border-tf-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1877f2] flex items-center justify-center">
            <Icons.Facebook className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-tf-text">Connect Meta Ads Account</h3>
            <p className="text-sm text-tf-text-muted">Enter your credentials to connect</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-lg">
            <Icons.AlertCircle className="w-4 h-4 text-tf-feedback-error-text flex-shrink-0" />
            <p className="text-sm text-tf-feedback-error-text">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto text-tf-feedback-error-text hover:text-tf-feedback-error-text">
              <Icons.X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 1: Access Token */}
        <div>
          <label className="block text-sm font-medium text-tf-text mb-1.5">
            <span className="inline-flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-semibold">1</span>
              Access Token
            </span>
          </label>
          <div className="flex gap-2">
            <textarea
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="Paste your Meta API access token"
              rows={3}
              className="flex-1 px-3 py-2 border border-tf-border rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
            />
          </div>
          <p className="mt-1.5 text-xs text-tf-text-muted">
            Get this from{' '}
            <a
              href="https://developers.facebook.com/tools/explorer/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Graph API Explorer
            </a>
            {' '}with <code className="px-1 py-0.5 bg-tf-bg-muted rounded text-tf-text">ads_read</code> permission
          </p>
        </div>

        {/* Fetch Accounts Button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleFetchAccounts}
            disabled={!accessToken || isFetchingAccounts}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary bg-tf-feedback-info-bg rounded-lg hover:bg-tf-feedback-info-bg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetchingAccounts ? (
              <>
                <Icons.Loader2 className="w-4 h-4 animate-spin" />
                Fetching...
              </>
            ) : (
              <>
                <Icons.Search className="w-4 h-4" />
                Fetch Ad Accounts
              </>
            )}
          </button>
          <span className="text-xs text-tf-text-muted">or enter the account ID manually below</span>
        </div>

        {/* Account Selector */}
        {showAccountSelector && adAccounts.length > 0 && (
          <div className="border border-tf-border rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-tf-bg-muted border-b border-tf-border">
              <p className="text-sm font-medium text-tf-text">Select an Ad Account</p>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {adAccounts.map((account) => (
                <button
                  key={account.id}
                  onClick={() => handleSelectAccount(account)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-tf-bg-muted transition-colors border-b border-tf-border last:border-b-0"
                >
                  <div className="text-left">
                    <p className="text-sm font-medium text-tf-text">{account.name}</p>
                    <p className="text-xs text-tf-text-muted">ID: {account.account_id} • {account.currency}</p>
                  </div>
                  <Icons.ChevronRight className="w-4 h-4 text-tf-text-muted" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Ad Account ID */}
        <div>
          <label className="block text-sm font-medium text-tf-text mb-1.5">
            <span className="inline-flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-primary text-white text-xs flex items-center justify-center font-semibold">2</span>
              Ad Account ID
            </span>
          </label>
          <input
            type="text"
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
            placeholder="e.g., 220268500"
            className="w-full px-3 py-2 border border-tf-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          />
          <p className="mt-1.5 text-xs text-tf-text-muted">
            Enter the numeric ID only (without <code className="px-1 py-0.5 bg-tf-bg-muted rounded text-tf-text">act_</code> prefix)
          </p>
        </div>

        {/* Connect Button */}
        <button
          onClick={handleConnect}
          disabled={isConnecting || !accessToken || !adAccountId}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isConnecting ? (
            <>
              <Icons.Loader2 className="w-4 h-4 animate-spin" />
              Connecting...
            </>
          ) : (
            <>
              <Icons.Link className="w-4 h-4" />
              Connect Account
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Connected State Component
// ============================================================================

function ConnectedState() {
  const { connection, disconnect, sync, smartSync, refresh } = useMetaConnection()
  const [isSyncing, setIsSyncing] = useState(false)
  const [isFullSyncing, setIsFullSyncing] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  // Smart Sync - Fast incremental sync (only missing data)
  const handleSmartSync = async () => {
    setIsSyncing(true)
    try {
      await smartSync()
      setTimeout(() => {
        refresh()
        setIsSyncing(false)
      }, 2000)
    } catch (err) {
      console.error('Smart sync failed:', err)
      setIsSyncing(false)
    }
  }

  // Full Sync - Complete 90-day refresh
  const handleFullSync = async () => {
    if (!confirm('Full Refresh will re-sync all data from the last 90 days. This may take several minutes. Continue?')) {
      return
    }
    setIsFullSyncing(true)
    try {
      await sync()
      setTimeout(() => {
        refresh()
        setIsFullSyncing(false)
      }, 2000)
    } catch (err) {
      console.error('Full sync failed:', err)
      setIsFullSyncing(false)
    }
  }

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect your Meta account? This will remove all synced data.')) {
      return
    }

    setIsDisconnecting(true)
    try {
      await disconnect()
    } catch (err) {
      console.error('Disconnect failed:', err)
    } finally {
      setIsDisconnecting(false)
    }
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
      <div className="px-6 py-4 border-b border-tf-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1877f2] flex items-center justify-center">
              <Icons.Facebook className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-tf-text">Meta Ads Connection</h3>
              <p className="text-sm text-tf-text-muted">Manage your connected account</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-tf-feedback-success-bg text-tf-feedback-success-text">
            <Icons.CheckCircle2 className="w-3.5 h-3.5" />
            Connected
          </span>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Account Info */}
        <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wider mb-1">Account ID</p>
              <p className="text-sm font-semibold text-tf-text">{connection.accountId}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wider mb-1">Account Name</p>
              <p className="text-sm font-semibold text-tf-text">{connection.accountName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wider mb-1">Currency</p>
              <p className="text-sm font-semibold text-tf-text">{connection.currency || 'USD'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-tf-text-muted uppercase tracking-wider mb-1">Last Synced</p>
              <p className="text-sm font-semibold text-tf-text">
                {connection.lastSyncAt ? new Date(connection.lastSyncAt).toLocaleString() : 'Never'}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {/* Sync Buttons Row */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSmartSync}
              disabled={isSyncing || isFullSyncing}
              title="Syncs only missing data (yesterday, today, and gaps) - Fast"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {isSyncing ? (
                <>
                  <Icons.Loader2 className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Icons.Zap className="w-4 h-4" />
                  Smart Sync
                </>
              )}
            </button>
            <button
              onClick={handleFullSync}
              disabled={isSyncing || isFullSyncing}
              title="Re-fetches all data from the last 90 days - Slower"
              className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-tf-text-muted bg-tf-bg-muted rounded-lg hover:bg-tf-bg-hover transition-colors disabled:opacity-50"
            >
              {isFullSyncing ? (
                <>
                  <Icons.Loader2 className="w-4 h-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <Icons.RefreshCw className="w-4 h-4" />
                  Full Refresh
                </>
              )}
            </button>
          </div>
          {/* Disconnect Button */}
          <button
            onClick={handleDisconnect}
            disabled={isDisconnecting || isSyncing || isFullSyncing}
            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-tf-feedback-error-text bg-tf-feedback-error-bg rounded-lg hover:bg-tf-feedback-error-bg transition-colors disabled:opacity-50"
          >
            {isDisconnecting ? (
              <>
                <Icons.Loader2 className="w-4 h-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              <>
                <Icons.Unlink className="w-4 h-4" />
                Disconnect
              </>
            )}
          </button>
        </div>

        {/* Help Text */}
        <div className="p-4 bg-tf-feedback-info-bg rounded-lg border border-primary/20">
          <div className="flex gap-3">
            <Icons.Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-tf-text">Sync Options</p>
              <div className="text-xs text-tf-text-muted mt-1 space-y-1">
                <p>
                  <span className="font-medium text-tf-text">Smart Sync:</span> Fast update that only fetches missing data (yesterday, today, and gaps). Recommended for daily use.
                </p>
                <p>
                  <span className="font-medium text-tf-text">Full Refresh:</span> Re-fetches all data from the last 90 days. Use this if you notice data discrepancies or after reconnecting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Sync Modal Component
// ============================================================================

function SyncingModal() {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-tf-bg-card rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-tf-feedback-info-bg flex items-center justify-center mx-auto mb-6">
            <Icons.RefreshCw className="w-8 h-8 text-primary animate-spin" />
          </div>
          <h3 className="text-xl font-bold text-tf-text mb-2">Syncing Your Data</h3>
          <p className="text-sm text-tf-text-muted mb-6">
            We're fetching your campaign data from Meta. This may take a moment...
          </p>
          <div className="w-full bg-tf-bg-muted rounded-full h-2 mb-4 overflow-hidden">
            <div className="bg-primary h-full rounded-full animate-pulse w-3/4" />
          </div>
          <p className="text-xs text-tf-text-muted">
            Fetching campaigns, ad sets, and performance metrics
          </p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Financial Settings Section
// ============================================================================

function FinancialSettingsSection() {
  const { user } = useAuth()
  const [financialSettings, setFinancialSettings] = useState<FinancialSettings | null>(null)
  const [cogsInput, setCogsInput] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [loading, setLoading] = useState(true)

  // Fetch financial settings
  const fetchSettings = useCallback(async () => {
    if (!user?.tenantId) return

    try {
      setLoading(true)
      const response = await apiClient.get('/my-company/financial-settings')
      const settings = response.data.settings as FinancialSettings
      setFinancialSettings(settings)
      // Convert decimal to percentage for display (e.g., 0.45 -> 45)
      setCogsInput(settings.cogs_percentage > 0 ? String(Math.round(settings.cogs_percentage * 100)) : '')
    } catch (err) {
      console.error('[FinancialSettings] Error fetching settings:', err)
      setFinancialSettings(null)
      setCogsInput('')
    } finally {
      setLoading(false)
    }
  }, [user?.tenantId])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  // Save financial settings
  const handleSave = async () => {
    if (!user?.tenantId) return

    // Parse and validate input (user enters percentage like 45 for 45%)
    const percentageValue = parseFloat(cogsInput)
    if (isNaN(percentageValue) || percentageValue < 0 || percentageValue > 100) {
      alert('Please enter a valid percentage between 0 and 100')
      return
    }

    // Convert percentage to decimal (e.g., 45 -> 0.45)
    const decimalValue = percentageValue / 100

    try {
      setIsSaving(true)
      setSaveSuccess(false)
      await apiClient.put('/my-company/financial-settings', {
        cogs_percentage: decimalValue,
      })
      await fetchSettings()
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('[FinancialSettings] Error saving:', err)
      alert('Failed to save financial settings')
    } finally {
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6">
        <div className="flex items-center gap-3">
          <Icons.Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-sm text-tf-text-muted">Loading financial settings...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border overflow-hidden">
      <div className="px-6 py-4 border-b border-tf-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-tf-feedback-success-bg flex items-center justify-center">
            <Icons.DollarSign className="w-5 h-5 text-tf-feedback-success-text" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-tf-text">Financial Settings</h3>
            <p className="text-sm text-tf-text-muted">Configure cost calculations for analytics</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* COGS Input */}
        <div>
          <label className="block text-sm font-medium text-tf-text mb-1.5">
            Cost of Goods Sold (COGS) Percentage
          </label>
          <p className="text-xs text-tf-text-muted mb-3">
            Enter your average COGS as a percentage of revenue. This will be used to calculate Gross Profit in your marketing summary.
            For example, if your products cost 40% of their selling price, enter 40.
          </p>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-[200px]">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={cogsInput}
                onChange={(e) => {
                  setCogsInput(e.target.value)
                  setSaveSuccess(false)
                }}
                placeholder="0"
                className="w-full px-3 py-2 pr-8 border border-tf-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-tf-text-muted text-sm">%</span>
            </div>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <Icons.Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : saveSuccess ? (
                <>
                  <Icons.Check className="w-4 h-4" />
                  Saved!
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
          {financialSettings?.updated_at && (
            <p className="text-xs text-tf-text-muted mt-2">
              Last updated: {new Date(financialSettings.updated_at).toLocaleString()}
              {financialSettings.updated_by && ` by ${financialSettings.updated_by}`}
            </p>
          )}
        </div>

        {/* Info Box */}
        <div className="p-4 bg-tf-feedback-info-bg rounded-lg border border-primary/20">
          <div className="flex gap-3">
            <Icons.Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-tf-text">How COGS affects your metrics</p>
              <p className="text-xs text-tf-text-muted mt-1">
                The COGS percentage is deducted from your Gross Revenue before calculating Gross Profit.
              </p>
              <p className="text-xs text-primary mt-1 font-medium">
                Formula: Gross Profit = Gross Revenue - (Gross Revenue × COGS%) - Ad Spend
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Main Config Page
// ============================================================================

export default function MetaConfigPage() {
  const { connection } = useMetaConnection()

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Syncing Modal */}
      {connection.syncing && <SyncingModal />}

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-tf-text">Analytics Configuration</h1>
        <p className="text-sm text-tf-text-muted mt-1">
          Configure your Meta Ads connection and financial settings
        </p>
      </div>

      {/* Connection Status */}
      {connection.loading && !connection.syncing ? (
        <div className="bg-tf-bg-card rounded-xl border border-tf-border p-12 text-center">
          <Icons.Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-sm text-tf-text-muted">Checking connection status...</p>
        </div>
      ) : connection.connected ? (
        <ConnectedState />
      ) : (
        <ConnectionForm onSuccess={() => {}} />
      )}

      {/* Financial Settings */}
      <FinancialSettingsSection />

      {/* Additional Info */}
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-6">
        <h3 className="text-base font-semibold text-tf-text mb-4">About Meta Integration</h3>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-tf-bg-muted flex items-center justify-center flex-shrink-0">
              <Icons.BarChart3 className="w-4 h-4 text-tf-text-muted" />
            </div>
            <div>
              <p className="text-sm font-medium text-tf-text">Analytics Summary</p>
              <p className="text-xs text-tf-text-muted">View overall campaign performance and key metrics</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-tf-bg-muted flex items-center justify-center flex-shrink-0">
              <Icons.PieChart className="w-4 h-4 text-tf-text-muted" />
            </div>
            <div>
              <p className="text-sm font-medium text-tf-text">Budget Allocation</p>
              <p className="text-xs text-tf-text-muted">Monitor spend distribution and optimize budgets</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-tf-bg-muted flex items-center justify-center flex-shrink-0">
              <Icons.Image className="w-4 h-4 text-tf-text-muted" />
            </div>
            <div>
              <p className="text-sm font-medium text-tf-text">Creatives Analytics</p>
              <p className="text-xs text-tf-text-muted">Track creative performance and identify winners</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

